// Inline markdown for masterclass copy, shared by the default reader blocks
// and the article layout so the two cannot drift on what **bold** means.
//
// Lifted verbatim out of MasterclassReader.jsx. A leaf module with no imports,
// because the article renderer is imported BY the reader and a shared helper
// living in either one would be a cycle.

import React from "react";

/**
 * `->` becomes a drawn arrow.
 *
 * The NBA article is built on one sentence shape, used ten times: the thing
 * Coleman noticed, then the constraint he set for it. "I wanted to work on
 * two-foot solutions but at higher speeds -> if you get tagged, no bucket."
 * Typed as a hyphen and a greater-than it reads as a stray keystroke in the
 * middle of a sentence, which is what it looked like on the page.
 *
 * Drawn in `currentColor` rather than gold, so it works on a card, in a
 * video title and in body text, in both themes, without this leaf module
 * having to know anything about the palette. Decorative: the two halves of
 * the sentence read fine in sequence without it.
 */
function Arrow({ k, both }) {
  return (
    <svg key={k} width="16" height="10" viewBox="0 0 16 10" aria-hidden="true" focusable="false"
      style={{ display: "inline-block", verticalAlign: "-0.5px", margin: "0 7px", opacity: 0.6 }}>
      <path d={both ? "M2.5 5H13.5M10 1.5 13.75 5 10 8.5M6 1.5 2.25 5 6 8.5"
                    : "M0.75 5H13.5M10 1.5 13.75 5 10 8.5"}
        fill="none" stroke="currentColor"
        strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

/**
 * Split a plain run on `->` and `<->`, drawing an arrow in place of each.
 *
 * `<->` is matched FIRST and deliberately. It is a real thing in this copy,
 * "no failure <-> success feedback loop", and it contains `->`: handling the
 * single arrow first turned it into a stray less-than followed by an arrow
 * pointing the wrong way at half the meaning. Found on the rendered page,
 * not in the source, which is the only place it looks wrong.
 */
function withArrows(seg, keyPrefix) {
  if (!seg.includes("->")) return [<span key={keyPrefix}>{seg}</span>];
  const out = [];
  seg.split(/\s*(<->|->)\s*/g).forEach((piece, i) => {
    if (piece === "->" || piece === "<->") {
      out.push(<Arrow key={`${keyPrefix}-a-${i}`} both={piece === "<->"} />);
    } else if (piece) {
      out.push(<span key={`${keyPrefix}-t-${i}`}>{piece}</span>);
    }
  });
  return out;
}

/**
 * `[words](https://...)` becomes a link, the words underlined in the text's
 * own colour. Concept docs cite their research this way (Attractors,
 * 2026-10-12), and until now the brackets and the URL printed as they were
 * typed. Only http(s) URLs, so nothing in copy can become a script link.
 * Opens in a new tab; in the iPhone app Capacitor hands it to Safari.
 *
 * A bare URL links too, showing itself (Representative Learning Design,
 * 2026-10-12, cites as "[Study: https://doi.org/...]"). Trailing
 * punctuation and a closing bracket stay text, so the sentence around it
 * reads as typed.
 */
const LINK = /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)|(https?:\/\/[^\s\])]*[^\s\]).,;:])/g;
export function withLinks(seg, keyPrefix, rest = (t, k) => [<span key={k}>{t}</span>]) {
  if (!seg.includes("](") && !/https?:\/\//.test(seg)) return rest(seg, keyPrefix);
  const out = [];
  let last = 0, m, n = 0;
  LINK.lastIndex = 0;
  while ((m = LINK.exec(seg))) {
    if (m.index > last) out.push(...rest(seg.slice(last, m.index), `${keyPrefix}-t${n}`));
    out.push(
      <a key={`${keyPrefix}-l${n}`} href={m[2] || m[3]} target="_blank" rel="noopener noreferrer"
        style={{ color: "inherit", textDecoration: "underline", textUnderlineOffset: 3,
          textDecorationThickness: 1, textDecorationColor: "currentColor",
          overflowWrap: "anywhere" }}>{m[1] || m[3]}</a>
    );
    last = LINK.lastIndex;
    n += 1;
  }
  if (last < seg.length) out.push(...rest(seg.slice(last), `${keyPrefix}-t${n}`));
  return out;
}

/**
 * **bold**, then *single-asterisk* (also rendered bold).
 *
 * Italics on dark cards read as fragile, so they are promoted to bold rather
 * than rendered as italic. Bold runs first because its pattern is greedier;
 * the single-asterisk pass then scans only the leftover plain runs.
 */
export function renderInline(text) {
  const boldSplit = String(text).split(/(\*\*[^*]+\*\*)/g);
  const out = [];
  boldSplit.forEach((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      out.push(<strong key={`b-${i}`} style={{ fontWeight: 700, color: "inherit" }}>{part.slice(2, -2)}</strong>);
      return;
    }
    const inlineSplit = part.split(/(\*[^*\n]+\*)/g);
    inlineSplit.forEach((seg, j) => {
      if (seg.startsWith("*") && seg.endsWith("*") && seg.length > 2) {
        out.push(<strong key={`i-${i}-${j}`} style={{ fontWeight: 700, color: "inherit" }}>{seg.slice(1, -1)}</strong>);
      } else if (seg) {
        out.push(...withLinks(seg, `s-${i}-${j}`, withArrows));
      }
    });
  });
  return out;
}

export default renderInline;
